import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { Capacitor } from '@capacitor/core';
import { trackEvent } from '@/common/analytics';
import { PLAY_STORE_URL, detectMobileOs, storeUrlFor, type MobileOs } from '@/common/appStores';
import { buildAppSchemeUrl } from '@/native/deepLink';
import { claudeTokens } from '@/styles/claudeTokens';

/**
 * "명치사 앱에서 보기" 인터스티셜.
 *
 * 카카오톡·문자로 공유된 링크를 모바일 브라우저에서 열었을 때, 앱으로 이어서 볼 수 있게 안내하는 바텀시트다.
 * Universal Link 는 카카오톡 인앱 브라우저에서 가로채이므로 여기서는 **커스텀 스킴**(`myungchisa://`)을 쓴다.
 *
 * 노출 조건 — 아래를 모두 만족할 때만 뜬다.
 * - 네이티브 앱이 아님(`Capacitor.isNativePlatform()`)  · 터치 기반 모바일 브라우저(iOS/Android)
 * - 공유 가능한 공개 화면: 홈·검색·목록·상세(2026-09-18 홈/목록 추가). 로그인·마이페이지·작성 폼·앱 브릿지(`/app`)는 제외
 * - 이번 페이지 로드에서 아직 안 띄웠음 — 링크를 열 때마다 한 번씩 뜬다(인스타그램과 같은 방식).
 *   예전의 "세션당 1회 + 웹에서 보기 후 7일 억제"는 카카오톡 인앱 브라우저에서 한 번 닫으면 일주일간 안 떠
 *   공유 유입마다 앱 안내가 나가지 않아 걷어냈다(2026-09-17).
 */

/** 이번 페이지 로드에서 이미 노출했는지 — SPA 안에서 화면을 옮겨 다닐 때 반복해서 뜨지 않게 한다. */
let shownThisLoad: boolean = false;
/** 스킴 이동이 실패했다고 판단하기까지의 대기 시간. 앱이 뜨면 그 전에 페이지가 숨겨진다. */
const APP_OPEN_TIMEOUT_MS: number = 3000;
/** 타이머가 이보다 늦게 깨어났으면 그 사이 앱으로 전환됐던 것(사파리 정지) — 스토어로 보내지 않는다 */
const APP_SWITCH_DRIFT_MS: number = 800;

/** 공개 목록(`/posts`)과 상세(`/posts/123`)를 모두 가진 공유 대상 라우트의 첫 세그먼트. */
const SHAREABLE_PREFIXES: readonly string[] = ['posts', 'seminars', 'recruitments', 'expert-lounge', 'expert-qna', 'notices'];
/** 상세 id 자리에 올 수 있지만 실제로는 작성 폼·내 목록이라 공유 대상이 아닌 세그먼트(`/recruitments/region`·`welfare` 는 공개 목록이라 허용). */
const NON_SHAREABLE_SEGMENTS: readonly string[] = ['new', 'my', 'history', 'edit'];
/** 세그먼트 규칙 밖의 공개 화면 — 홈과 검색. */
const SHAREABLE_EXACT_PATHS: readonly string[] = ['/', '/search'];

/**
 * 공유해도 의미가 있는(비로그인도 열람 가능한) 화면인지 판별한다.
 *
 * @param pathname 현재 경로 (`useLocation().pathname`)
 */
function isShareableRoute(pathname: string): boolean {
    if (SHAREABLE_EXACT_PATHS.includes(pathname)) {
        return true;
    }
    if (pathname === '/salary-surveys' || pathname.startsWith('/salary-surveys/')) {
        return true;
    }
    const segments: string[] = pathname.split('/').filter(Boolean);
    if (segments.length === 0 || segments.length > 2 || !SHAREABLE_PREFIXES.includes(segments[0])) {
        return false;
    }
    // `/posts` 목록 또는 `/posts/123` 상세. 작성 폼·내 목록은 제외
    return segments.length === 1 || !NON_SHAREABLE_SEGMENTS.includes(segments[1]);
}

/**
 * Android 는 `intent://` 로 보내면 앱이 없을 때 크롬이 알아서 Play 스토어로 보낸다(타임아웃 폴백 불필요).
 * iOS 는 그런 장치가 없어 스킴을 시도한 뒤 화면이 그대로면 App Store 로 보낸다.
 */
function buildAndroidIntentUrl(path: string): string {
    const encodedPath: string = encodeURIComponent(path);
    const fallback: string = encodeURIComponent(PLAY_STORE_URL);
    return `intent://open?path=${encodedPath}#Intent;scheme=myungchisa;package=com.myungchisa.app;S.browser_fallback_url=${fallback};end`;
}

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(31, 27, 22, 0.45);
    z-index: 1400;
    display: flex;
    align-items: flex-end;
    animation: mcFadeIn 160ms ease-out;

    @keyframes mcFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const Sheet = styled.div`
    width: 100%;
    background: ${claudeTokens.color.surface};
    border-radius: ${claudeTokens.radius.lg} ${claudeTokens.radius.lg} 0 0;
    padding: 20px 20px calc(20px + env(safe-area-inset-bottom));
    font-family: ${claudeTokens.font.body};
    animation: mcSlideUp 220ms ease-out;

    @keyframes mcSlideUp {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
    }
`;

const Wordmark = styled.span`
    font-size: 20px;
    font-weight: 800;
    color: ${claudeTokens.color.text1};
    letter-spacing: -0.03em;
    line-height: 1;

    &::after {
        content: '';
        display: inline-block;
        width: 0.32em;
        height: 0.32em;
        margin-left: 0.12em;
        background: ${claudeTokens.color.accent};
        border-radius: 50%;
    }
`;

const Title = styled.h2`
    font-size: 17px;
    font-weight: 700;
    color: ${claudeTokens.color.text1};
    margin: 12px 0 4px;
`;

const Desc = styled.p`
    font-size: 13px;
    line-height: 1.6;
    color: ${claudeTokens.color.text2};
    margin: 0 0 16px;
`;

const Actions = styled.div`
    display: flex;
    gap: 8px;
`;

const BaseButton = styled.button`
    flex: 1;
    height: 48px;
    border-radius: ${claudeTokens.radius.md};
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
`;

const PrimaryButton = styled(BaseButton)`
    border: none;
    background: ${claudeTokens.color.accent};
    color: #fff;
`;

const SecondaryButton = styled(BaseButton)`
    border: 1px solid ${claudeTokens.color.border};
    background: ${claudeTokens.color.surface};
    color: ${claudeTokens.color.text2};
`;

const OpenInAppSheet: React.FC = () => {
    const { pathname, search } = useLocation();
    const [visible, setVisible] = useState<boolean>(false);
    const osRef = useRef<MobileOs>('other');
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        if (visible || Capacitor.isNativePlatform() || !isShareableRoute(pathname)) {
            return;
        }
        const os: MobileOs = detectMobileOs();
        if (os === 'other' || shownThisLoad) {
            return;
        }
        osRef.current = os;
        shownThisLoad = true;
        setVisible(true);
        trackEvent('OPEN_IN_APP_SHEET_VIEW', { path: pathname, targetId: os });
    }, [pathname, visible]);

    // 앱 전환으로 페이지를 떠나면 스토어 폴백을 취소한다
    useEffect(() => {
        return () => {
            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
            }
        };
    }, []);

    const openInApp = useCallback((): void => {
        const os: MobileOs = osRef.current;
        const path: string = `${pathname}${search}`;
        trackEvent('OPEN_IN_APP_SHEET_OPEN', { path: pathname, targetId: os });
        setVisible(false);

        if (os === 'android') {
            // 크롬이 미설치 시 Play 스토어로 폴백해 준다
            window.location.href = buildAndroidIntentUrl(path);
            return;
        }

        // iOS: 스킴을 시도하고, 화면이 그대로면(앱 미설치) App Store 로 보낸다.
        // 사파리는 스킴을 열기 전에 "명치사에서 열겠습니까?" 확인창을 띄우는데, 그동안에도 타이머는 돈다.
        // 그래서 (1) 타이머를 3초로 두고 (2) 앱으로 전환돼 사파리가 멈췄다가 늦게 깨어난 경우(드리프트)와
        // (3) 확인창/앱 전환으로 blur·visibilitychange·pagehide 가 온 경우에는 스토어로 보내지 않는다.
        let left: boolean = false;
        const startedAt: number = Date.now();

        function cleanup(): void {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('blur', onBlur);
        }

        function onBlur(): void {
            left = true;
            cleanup();
        }

        function onVisibilityChange(): void {
            if (document.hidden) {
                left = true;
                cleanup();
            }
        }

        function onPageHide(): void {
            left = true;
            cleanup();
        }

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', onPageHide);
        window.addEventListener('blur', onBlur);

        window.location.href = buildAppSchemeUrl(path);

        timerRef.current = window.setTimeout(() => {
            timerRef.current = null;
            cleanup();
            const drifted: boolean = Date.now() - startedAt > APP_OPEN_TIMEOUT_MS + APP_SWITCH_DRIFT_MS;
            if (left || document.hidden || drifted) {
                return;
            }
            const storeUrl: string | null = storeUrlFor(os);
            if (storeUrl) {
                window.location.href = storeUrl;
            }
        }, APP_OPEN_TIMEOUT_MS);
    }, [pathname, search]);

    // 닫기만 한다 — 다음에 링크를 다시 열면 또 안내한다
    const stayOnWeb = useCallback((): void => {
        trackEvent('OPEN_IN_APP_SHEET_DISMISS', { path: pathname, targetId: osRef.current });
        setVisible(false);
    }, [pathname]);

    if (!visible) {
        return null;
    }

    return (
        <Backdrop onClick={stayOnWeb} role="presentation">
            <Sheet onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="명치사 앱에서 보기">
                <Wordmark>명치사</Wordmark>
                <Title>명치사 앱에서 보기</Title>
                <Desc>알림까지 놓치지 않고 받아볼 수 있어요.</Desc>
                <Actions>
                    <SecondaryButton type="button" onClick={stayOnWeb}>웹에서 보기</SecondaryButton>
                    <PrimaryButton type="button" onClick={openInApp}>앱으로 보기</PrimaryButton>
                </Actions>
            </Sheet>
        </Backdrop>
    );
};

export default OpenInAppSheet;
